import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3500';

/** Timeout aperçu PDF (long-poll agent ≤ 45 s) + marge réseau. */
export const U3500_DOCUMENT_CONTENT_TIMEOUT_MS = 50_000;

const LogicalUnitEndpoints = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH_DOCUMENTS: `${LOGICALUNIT_URL}/documents/search`,
  PROJECT_DOCUMENTS_TREE: (projectId: string) => `${LOGICALUNIT_URL}/projects/${projectId}/documents/tree`,
  PROJECT_DELIVERABLE_CHECKLIST: (projectId: string) =>
    `${LOGICALUNIT_URL}/projects/${projectId}/deliverables/checklist`,
  PROJECT_SCAN: (projectId: string) => `${LOGICALUNIT_URL}/projects/${projectId}/scan`,
  DOCUMENT: (documentId: string) => `${LOGICALUNIT_URL}/documents/${documentId}`,
  DOCUMENT_CONTENT: (documentId: string) => `${LOGICALUNIT_URL}/documents/${documentId}/content`,
} as const;

export type U3500DocumentTreeNodeKind = 'folder' | 'file';

export interface U3500DocumentTreeNode {
  name: string;
  kind: U3500DocumentTreeNodeKind;
  relativePath: string;
  children?: U3500DocumentTreeNode[];
  documentId?: string;
  fileName?: string;
  sizeBytes?: string;
  mtime?: string;
  extension?: string;
  previewable?: boolean;
}

/** Statut synchro NAS → catalogue (BL-0483). */
export type U3500NasSyncStatus = 'never' | 'success' | 'error' | 'pending';

export interface U3500NasSync {
  lastSyncedAt: string | null;
  status: U3500NasSyncStatus;
  errorReason: string | null;
}

export interface U3500DocumentTreeResponse {
  projectId: string;
  projetNumber: string;
  rootPath: string;
  nodes: U3500DocumentTreeNode[];
  nasSync: U3500NasSync;
}

/** Kind livrable v1 (matrice natures → checklist BL-0476). */
export type U3500DeliverableKind = 'CL' | 'DT' | 'PIQ' | 'OCTR';

export type U3500DeliverableStatus = 'present' | 'missing';

export interface U3500DeliverableChecklistItem {
  kind: U3500DeliverableKind;
  status: U3500DeliverableStatus;
  matchedDocumentId: string | null;
  matchedRelativePath: string | null;
  matchedFileName: string | null;
  ruleId: string;
}

export interface U3500DeliverableChecklistResponse {
  projectId: string;
  projetNumber: string;
  workNatureCodes: string[];
  items: U3500DeliverableChecklistItem[];
}

/** Consigne GED (réponse minimale POST …/scan). */
export interface U3500ConsigneDto {
  id: string;
  status: string;
  type: string;
  projetNumber?: string | null;
}

export interface U3500DocumentMeta {
  documentId: string;
  projectId: string;
  projetNumber: string;
  rootPath: string;
  relativePath: string;
  fileName: string;
  sizeBytes: string;
  mtime: string;
  contentHash: string | null;
  uncPath: string;
  extension: string;
  previewable: boolean;
}

export type U3500DocumentMatchSource = 'path' | 'projectNumber' | 'projectLot';

export interface U3500DocumentSearchItem {
  id: string;
  documentId: string | null;
  projectId: string | null;
  projetNumber: string | null;
  projetName: string | null;
  relativePath: string;
  fileName: string | null;
  folderProjetNumber: string | null;
  matchSource: U3500DocumentMatchSource;
  score: number;
}

export interface U3500InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
}

export const defaultU3500InitLU: U3500InitLU = {
  launchSearch: false,
  hideSearch: false,
  nbRecordPerPage: 25,
};

type U3500DocumentSearchItemApi = Omit<U3500DocumentSearchItem, 'id'>;

/** Identifiant de ligne grille : documentId sinon clé synthétique. */
export function u3500BuildDocumentSearchRowId(item: U3500DocumentSearchItemApi, index: number): string {
  if (item.documentId) {
    return item.documentId;
  }
  return `hit-${item.projectId ?? 'orphan'}-${item.relativePath}-${item.matchSource}-${index}`;
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

/**
 * Convertit la pagination FE (1-based) vers l’API U3500 documents/search (0-based),
 * et remappe la réponse + `id` pour la grille.
 */
export const doSearchDocuments = async (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  const fePage = searchPayload.pagination?.page ?? 1;
  const apiPayload: U2000SearchPayload = {
    ...searchPayload,
    pagination: {
      ...searchPayload.pagination,
      page: Math.max(0, fePage - 1),
      size: searchPayload.pagination?.size ?? 25,
    },
  };

  const response = await axios.post(LogicalUnitEndpoints.SEARCH_DOCUMENTS, apiPayload);
  const results = (response.data?.results ?? []) as U3500DocumentSearchItemApi[];
  const mapped: U3500DocumentSearchItem[] = results.map((item, index) => ({
    ...item,
    id: u3500BuildDocumentSearchRowId(item, index),
  }));

  return {
    ...response,
    data: {
      ...response.data,
      results: mapped,
      input: {
        ...response.data?.input,
        filter: {
          criterias: response.data?.input?.filter?.criterias ?? searchPayload.filter?.criterias ?? [],
          fields: response.data?.input?.filter?.fields ?? searchPayload.filter?.fields ?? [],
          sortedFields: response.data?.input?.filter?.sortedFields ?? searchPayload.filter?.sortedFields ?? [],
        },
        pagination: {
          ...response.data?.input?.pagination,
          page: fePage,
          size: apiPayload.pagination.size,
        },
      },
    },
  };
};

export const doGetProjectDocumentTree = async (axios: AxiosInstance, projectId: string) => {
  const response = await axios.get(LogicalUnitEndpoints.PROJECT_DOCUMENTS_TREE(projectId), {
    params: { b: U2000GenerateUrlUUID() },
  });
  return (response.data?.result ?? response.data) as U3500DocumentTreeResponse;
};

/**
 * Checklist livrables (présent/manquant) d’après natures + catalogue NAS.
 * GET U3500/projects/:id/deliverables/checklist
 */
export const doGetProjectDeliverableChecklist = async (
  axios: AxiosInstance,
  projectId: string
): Promise<U3500DeliverableChecklistResponse> => {
  const response = await axios.get(LogicalUnitEndpoints.PROJECT_DELIVERABLE_CHECKLIST(projectId), {
    params: { b: U2000GenerateUrlUUID() },
  });
  return (response.data?.result ?? response.data) as U3500DeliverableChecklistResponse;
};

/**
 * Enqueue un SCAN inventory mono-projet (POST U3500/projects/:id/scan, body vide).
 */
export const doStartProjectScan = async (
  axios: AxiosInstance,
  projectId: string
): Promise<U3500ConsigneDto> => {
  const response = await axios.post(LogicalUnitEndpoints.PROJECT_SCAN(projectId.trim()), {});
  return (response.data?.result ?? response.data) as U3500ConsigneDto;
};

export const doGetDocumentMeta = async (axios: AxiosInstance, documentId: string) => {
  const response = await axios.get(LogicalUnitEndpoints.DOCUMENT(documentId), {
    params: { b: U2000GenerateUrlUUID() },
  });
  return (response.data?.result ?? response.data) as U3500DocumentMeta;
};

export const doGetDocumentContent = (axios: AxiosInstance, documentId: string) => {
  return axios.get(LogicalUnitEndpoints.DOCUMENT_CONTENT(documentId), {
    params: { b: U2000GenerateUrlUUID() },
    responseType: 'blob',
    timeout: U3500_DOCUMENT_CONTENT_TIMEOUT_MS,
    skipBusyIndicator: true,
  });
};

/**
 * Si l’erreur API arrive en Blob (responseType blob), parse le JSON pour U2000HandleApiError.
 */
export async function u3500NormalizeApiError(error: unknown): Promise<unknown> {
  const err = error as { status?: number; data?: unknown; code?: string; message?: string };
  if (!(err?.data instanceof Blob)) {
    return error;
  }
  try {
    const text = await err.data.text();
    const parsed = JSON.parse(text) as unknown;
    return {
      status: err.status ?? 0,
      code: err.code ?? '',
      message: err.message ?? '',
      data: parsed,
    };
  } catch {
    return error;
  }
}

export function luHandleAppiError(
  t: TranslateFn,
  NS: string,
  err: { errorCode?: string; description?: string },
  errorResult: U2000ApiErrorResult
): void {
  if (err.errorCode === 'BU350008') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('errorBU350008', { ns: NS }),
    });
  }
  if (err.errorCode === 'BU350009') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('errorBU350009', { ns: NS }),
    });
  }
  if (err.errorCode === 'BU350006') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('errorBU350006', { ns: NS }),
    });
  }
  if (err.errorCode === 'BU350014') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('errorBU350014', { ns: NS }),
    });
  }
  if (err.errorCode === 'BU312101') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: NS }),
      text: t('errorBU312101', { ns: NS }),
    });
  }
}
